// Keep these limits aligned with the public quote intake in backend/server.py.
export const QUOTE_PHOTO_BYTES = 1024 * 1024;
export const QUOTE_PHOTO_TOTAL_BYTES = 6 * QUOTE_PHOTO_BYTES;
export const QUOTE_PHOTOS_PER_SECTION = 6;
const FIELDS = ["photos", "backyard_photos", "inspiration_photos"];
const TYPES = ["image/jpeg", "image/png", "image/webp"];

// Reserve bytes/counts synchronously, including readers still in flight. This
// prevents overlapping drops in different sections from exceeding the budget.
export function createQuotePhotoUploads({ onChange, onError, makeReader = () => new FileReader() }) {
  let galleries = Object.fromEntries(FIELDS.map(field => [field, []]));
  const pending = new Map();
  const fail = (key, file) => onError(key, file.name);
  const usedBytes = () => Object.values(galleries).flat().reduce((sum, photo) => sum + photo.bytes, 0)
    + [...pending.values()].reduce((sum, item) => sum + item.file.size, 0);

  return {
    add(field, files) {
      if (!FIELDS.includes(field)) return;
      for (const file of Array.from(files || [])) {
        if (!TYPES.includes(file.type) || !file.size) {
          fail("photo_invalid", file);
          continue;
        }
        if (file.size > QUOTE_PHOTO_BYTES) {
          fail("photo_too_large", file);
          continue;
        }
        const count = galleries[field].length + [...pending.values()].filter(item => item.field === field).length;
        if (count >= QUOTE_PHOTOS_PER_SECTION) {
          fail("photo_too_many", file);
          continue;
        }
        if (usedBytes() + file.size > QUOTE_PHOTO_TOTAL_BYTES) {
          fail("photo_total_too_large", file);
          continue;
        }
        const reader = makeReader();
        pending.set(reader, { field, file });
        reader.onload = () => {
          if (!pending.delete(reader)) return; // A close/reset cancelled this read.
          if (typeof reader.result !== "string") {
            fail("photo_read_error", file);
            return;
          }
          galleries[field] = [...galleries[field], { value: reader.result, bytes: file.size }];
          onChange(field, galleries[field].map(photo => photo.value));
        };
        reader.onerror = () => {
          if (pending.delete(reader)) fail("photo_read_error", file);
        };
        reader.onabort = () => pending.delete(reader);
        try {
          reader.readAsDataURL(file);
        } catch (_) {
          if (pending.delete(reader)) fail("photo_read_error", file);
        }
      }
    },
    remove(field, index) {
      if (!FIELDS.includes(field)) return;
      galleries[field] = galleries[field].filter((_, i) => i !== index);
      onChange(field, galleries[field].map(photo => photo.value));
    },
    reset() {
      const readers = [...pending.keys()];
      pending.clear();
      galleries = Object.fromEntries(FIELDS.map(field => [field, []]));
      readers.forEach(reader => reader.abort());
    },
  };
}
